// Mail→Vorgang SP3 — pure, DOM-/Fetch-freie Logik des Light-Erstell-Popups
// (VorgangCreateFlowWindow). Bewusst aus der Komponente herausgelöst, damit
// KV-Auflösung, Termine- und Payload-Bau in der Vitest-Suite testbar sind
// (Muster linkSlots.ts / mailCommandState.ts).
import { fristZurTwv } from "../shared/fristZurTwv";
import { FREITEXT_ART, betreffFuerArt } from "../../../data/vorgangArt";
import type { LinkSlots } from "../detail/linkSlots";
import type { LinkedEntityType, TwvZielgruppe } from "../../../services/vorgangService";

/** Team-Subset aus `GET /rbac/teams-lite` (Name + Teamleiter). */
export type TeamLite = { id: string; name: string; leadUserIds?: string[] };

export const dedupe = (ids: string[]): string[] => Array.from(new Set(ids.filter(Boolean)));

/*
 * ENTFALLEN (PO 04.09.2026): `WEITERE_OPTIONEN_DEFAULT_OFFEN` — „Zu
 * informierende Personen" lag hinter einem Ausklapper „weitere Optionen"
 * (C6, Vorplanung §3.3). Das Feld steht jetzt direkt im Beteiligten-Block;
 * der Ausklapper enthielt nichts sonst und ist ganz entfallen.
 */

/**
 * Die Vorgangsart, die WIRKLICH gilt.
 *
 * Ein persönlicher Vorgang ist immer „Sonstige" (PO 04.09.2026: „neuer
 * Vorgang, bei persönlicher Vorgang Vorgangsart = Sonstiges"). Der Startwert
 * stimmte schon; die Regel greift für den Fall, dass jemand erst NKG/JG/GAB
 * wählt und DANN auf „persönlich" umschaltet — `wechsleZuweisungsart` setzte
 * Slots, KV und Beteiligte zurück, die Art nicht.
 *
 * Als Ableitung statt als State-Sync: sonst gäbe es zwei Wahrheiten, und die
 * Anzeige könnte von dem abweichen, was gesendet wird. ALLE Verbraucher der
 * Art lesen das Ergebnis dieser Funktion.
 *
 * Bewusst `FREITEXT_ART` und nicht `DEFAULT_VORGANG_ART`: letzteres ist als
 * „Vorbelegung für neue Vorgänge" dokumentiert und darf sich ändern; die
 * Invariante hier hängt an der Art „Sonstige" selbst.
 */
export function artFuerZuweisung(persoenlich: boolean, gewaehlt: string): string {
    return persoenlich ? FREITEXT_ART : gewaehlt;
}

/** Kontakt-Subset für die KV-Auflösung (FO-User + BO-Slot USER/TEAM). */
export interface KvContactLike {
    foUserId?: string | null;
    boType?: string | null;
    boUserId?: string | null;
    boTeamId?: string | null;
}

export type KvBo =
    | { type: "TEAM"; teamId: string; teamName: string }
    | { type: "USER"; userId: string };

export interface KvResolution {
    /** Frontoffice-User (Kontaktverantwortlicher) — null, wenn nicht gesetzt. */
    foUserId: string | null;
    /** Backoffice-Slot: Team oder User — null bei boType NONE/leer. */
    bo: KvBo | null;
    /** HV-Vorbelegung: Teamleiter des BO-Teams bzw. der BO-User (wie bisher). */
    hvPrefill: string[];
}

/**
 * C5: Kontaktverantwortlichkeit auflösen — jetzt inkl. FO (vorher nur BO).
 * Die HV-Vorbelegung bleibt BO-basiert (Teamleiter bzw. BO-User, kein
 * FO-Fallback — Entscheidung aus dem Create-Rework).
 */
export function resolveKv(contact: KvContactLike, teams: TeamLite[]): KvResolution {
    const foUserId = contact.foUserId?.trim() ? contact.foUserId : null;
    if (contact.boType === "TEAM" && contact.boTeamId) {
        const team = teams.find((t) => t.id === contact.boTeamId);
        return {
            foUserId,
            bo: { type: "TEAM", teamId: contact.boTeamId, teamName: team?.name ?? "Team" },
            hvPrefill: dedupe(team?.leadUserIds ?? []),
        };
    }
    if (contact.boType === "USER" && contact.boUserId) {
        return {
            foUserId,
            bo: { type: "USER", userId: contact.boUserId },
            hvPrefill: [contact.boUserId],
        };
    }
    return { foUserId, bo: null, hvPrefill: [] };
}

/**
 * Text der KV-Pill: `FO: <Kürzel> · BO: <Team/Kürzel>` — fehlende Teile werden
 * ausgelassen, ganz ohne FO/BO bleibt „—". `kuerzel` löst eine User-id in das
 * KMVS-Kürzel auf (Muster vorgang/shared/people.ts).
 */
export function kvPillText(kv: KvResolution, kuerzel: (userId: string) => string): string {
    const parts: string[] = [];
    if (kv.foUserId) parts.push(`FO: ${kuerzel(kv.foUserId)}`);
    if (kv.bo?.type === "TEAM") parts.push(`BO: ${kv.bo.teamName}`);
    else if (kv.bo?.type === "USER") parts.push(`BO: ${kuerzel(kv.bo.userId)}`);
    return parts.length ? parts.join(" · ") : "—";
}

/**
 * Heute als `YYYY-MM-DD` in ORTSZEIT — das Format des `<input type="date">`.
 * Bewusst nicht `toISOString()`: das rechnet in UTC, und zwischen Mitternacht
 * und 2 Uhr stünde dort noch der Vortag.
 */
export function heuteIso(jetzt: Date = new Date()): string {
    const mm = String(jetzt.getMonth() + 1).padStart(2, "0");
    const dd = String(jetzt.getDate()).padStart(2, "0");
    return `${jetzt.getFullYear()}-${mm}-${dd}`;
}

/**
 * Hauptverantwortliche beim Anlegen (PO 28.09.2026: „HV ist vorbelegt und wenn
 * nicht, dann bin ich das"): die Vorbelegung aus der Kontaktverantwortlichkeit,
 * sonst der Anlegende selbst. Ohne angemeldeten Nutzer bleibt die Liste leer —
 * dann setzt der Server den Anleger ein.
 */
export function hvVorbelegung(hvPrefill: string[], ich: string | null): string[] {
    if (hvPrefill.length > 0) return dedupe(hvPrefill);
    return ich ? [ich] : [];
}

/**
 * TWV und Frist beim Anlegen gleichsetzen (PO 28.09.2026: „Beim Erstellen eines
 * Vorgangs TWV und Frist gleichsetzen"): Solange die Frist nicht bewusst
 * geändert wurde, folgt sie der TWV. Rein, damit die Regel testbar ist.
 */
export function fristNachTwv(
    neueTwv: string | null,
    frist: string | null,
    fristGeaendert: boolean,
): string | null {
    // T-331: Auch eine bewusst gesetzte Frist liegt nie vor der TWV.
    return fristGeaendert ? fristZurTwv(neueTwv, frist) : neueTwv;
}

/** Termine-Anteil des Payloads. */
export interface TerminePayload {
    twv?: string;
    frist?: string;
}

/**
 * C7: TWV und Frist sind UNABHÄNGIG setzbar und landen BEIDE im Payload
 * (vorher bedingt entweder/oder über `erstellerBeteiligt` — abgeschafft).
 */
export function buildTermine(twv: string | null, frist: string | null): TerminePayload {
    const out: TerminePayload = {};
    if (twv) out.twv = twv;
    if (frist) out.frist = frist;
    return out;
}

/** Formular-Zustand des Flows, soweit der Payload-Bau ihn braucht. */
export interface CreateFlowState {
    betreff: string;
    vorgangsart: string;
    slots: LinkSlots;
    hv: string[];
    /** D1: „weitere Bearbeiter" (früher „Mitarbeiter" genannt); fließt ins Payload-Feld `bearbeiter`. */
    bearbeiter: string[];
    zuInformierende: string[];
    twv: string | null;
    frist: string | null;
    twvZielgruppe?: TwvZielgruppe | null;
    ersteller?: string | null;
    linkedEntityType?: LinkedEntityType | null;
    linkedEntityId?: number | null;
    /**
     * I1/E07: persönlicher Vorgang — ohne Kontakt, Risiko und Vertrag, und
     * ausschließlich für seinen Ersteller sichtbar. Optional, damit bestehende
     * Aufrufer (Mail-, Kontakt-, Vertrags-Flow) unverändert bleiben.
     */
    persoenlich?: boolean;
}

/*
 * ENTFALLEN (PO 28.09.2026): `step1Fertig` — die Pflicht, vor dem Anlegen einen
 * Kontakt zu wählen. „Schnellanlage … und dann keine Restriktionen, also kein
 * ‚ich muss etwas ausfüllen um anzulegen'." Ein Vorgang darf seitdem ohne
 * Kontakt und ohne Betreff entstehen; der Server lässt beides zu.
 */

/**
 * Payload für `POST /vorgang` (neuer Vorgang, ohne Ziel-Vorgang).
 *
 * Der Betreff ist der eingegebene Freitext; bleibt er leer, springt bei
 * NKG/JG/GAB das Kürzel ein (betreffFuerArt, PO 02.08.2026). Das Backend
 * normalisiert dieselbe Regel nochmals final.
 *
 * I4 (Befund, 23.08.2026): Bis hierher wurden NUR `riskObjectId`/`contractId`
 * im SINGULAR gesendet. Serverseitig setzt `reconcileMultiLinks`
 * (apps/api/src/vorgang/multi-link-slots.ts) das Array dann auf genau diesen
 * einen Wert — alle weiteren im Dialog gewählten Risiken/Verträge fielen still
 * weg. Jetzt gewinnen die ARRAYS („Array im Payload → Quelle der Wahrheit");
 * die Singular-Felder bleiben für Back-Compat drin und werden serverseitig
 * ohnehin aus Position 0 gespiegelt.
 */
export function buildVorgangPayload(s: CreateFlowState): Record<string, unknown> {
    const basis = {
        titel: betreffFuerArt(s.vorgangsart, s.betreff),
        vorgangsart: s.vorgangsart,
        hauptverantwortlich: s.hv,
        bearbeiter: s.bearbeiter,
        ...buildTermine(s.twv, s.frist),
    };
    if (s.persoenlich) {
        // E07: keine Bezüge, keine zu Informierenden. Das Backend erzwingt beides
        // noch einmal (400 „Ein persönlicher Vorgang trägt keine Bezüge."); hier
        // wird es gar nicht erst gesendet.
        return { ...basis, persoenlich: true, zuInformierende: [] };
    }
    // Ohne Kontakt (PO 28.09.2026) entsteht ein Vorgang ohne Kunden. Mehrere
    // gewählte Kontakte gehen als `contactIds` mit — Position 0 ist der
    // Kunde; bis hierher fielen die weiteren beim Anlegen still weg.
    const kontakte = s.slots.kontakt
        ? [s.slots.kontakt, ...(s.slots.kontakte ?? []).filter((k) => k.id !== s.slots.kontakt!.id)]
        : [];
    return {
        ...basis,
        ...(s.slots.kontakt
            ? { kunde: { id: s.slots.kontakt.id }, contactIds: kontakte.map((k) => k.id) }
            : {}),
        riskObjectId: s.slots.risiko?.id ?? null,
        contractId: s.slots.vertrag?.id ?? null,
        riskObjectIds: s.slots.risiken.map((r) => r.id),
        contractIds: s.slots.vertraege.map((v) => v.id),
        zuInformierende: s.zuInformierende,
    };
}

/**
 * Payload für `POST /todo` (Zuweisung an bestehenden Vorgang → das Erstellte
 * ist ein Todo; Todo kennt kein HV-Feld — HV fließt in die Bearbeiter).
 */
export function buildTodoInVorgangPayload(
    s: CreateFlowState,
    vorgangId: number,
): Record<string, unknown> {
    return {
        titel: s.betreff.trim(),
        vorgangId,
        // Kontakt ist optional: ein Todo in einem Vorgang ohne Kontaktbezug
        // (persönlicher Vorgang, I1/E07) hat keinen — `slots.kontakt!.id` würde
        // dort werfen.
        contactId: s.slots.kontakt?.id ?? null,
        riskObjectId: s.slots.risiko?.id ?? null,
        contractId: s.slots.vertrag?.id ?? null,
        // I4: siehe buildVorgangPayload — die Arrays tragen ALLE gewählten Bezüge.
        riskObjectIds: s.slots.risiken.map((r) => r.id),
        contractIds: s.slots.vertraege.map((v) => v.id),
        bearbeiter: dedupe([...s.hv, ...s.bearbeiter]),
        zuInformierende: s.zuInformierende,
        status: "Offen",
        ersteller: s.ersteller ?? undefined,
        ...(s.twvZielgruppe ? { twvZielgruppe: s.twvZielgruppe } : {}),
        ...(s.linkedEntityType && s.linkedEntityId != null
            ? { linkedEntityType: s.linkedEntityType, linkedEntityId: s.linkedEntityId }
            : {}),
        ...buildTermine(s.twv, s.frist),
    };
}

/*
 * ENTFALLEN (PO 23.08.2026): `vorgangsartAusKontaktstatus` — die Vorauswahl der
 * Vorgangsart aus dem Kontaktstatus (Interessent → NKG, Kunde → GAB, E3).
 *
 * Die Regel schloss vom STATUS des erkannten Kontakts auf die ABSICHT der Mail.
 * Das trifft selten zu: der Kunde schreibt meistens nicht wegen einer
 * Gesamt-Ausschreibung. Vorbelegt wird deshalb wieder ausschließlich
 * `DEFAULT_VORGANG_ART` („Sonstige", data/vorgangArt.ts) — der Auffang-Wert,
 * den der Nutzer in einem Griff ändern kann.
 *
 * Wer die Status-Vorauswahl zurückholen will, braucht beides wieder: die
 * Abbildung hier UND den Effekt im VorgangCreateFlowWindow, der sie nur im
 * einschrittigen Mail-Weg und nur vor der ersten manuellen Wahl anwendete.
 */

/* ------------------------------------------------------------------------- *
 * To-do-Entwürfe aus dem Erstell-Flow (T-39, PO 01.10.2026)
 *
 * Bis hierher übersprang `legeDraftTodosAn` jeden Entwurf ohne Betreff
 * wortlos — „Vorgang erstellen" meldete Erfolg, die Eingabe war weg. Seitdem
 * gilt: ein Entwurf ohne Betreff wird ANGELEGT (Ersatz-Titel aus der
 * Beschreibung, sonst „To-do"); nur eine völlig unberührte Karte entfällt,
 * und das sagt ein Hinweis.
 * ------------------------------------------------------------------------- */

/** Ein To-do-Entwurf, wie die Karte im Erstell-Flow ihn hält. */
export interface TodoDraftLike {
    key: number;
    titel: string;
    beschreibung: string;
    slots: LinkSlots;
    bearbeiter: string[];
    zuInformierende: string[];
    twv: string | null;
    frist: string | null;
}

/** Ersatz-Titel höchstens so lang (danach „…"). Gleiche Regel wie im Backend. */
export const TODO_ERSATZTITEL_MAX = 80;

/**
 * Titel eines Entwurfs: der getippte Betreff; ohne ihn die erste nicht-leere
 * Zeile der Beschreibung (auf 80 Zeichen gekürzt), sonst „To-do".
 *
 * Bewusst NIE der Vorgangsbetreff: `uebernimmVorgangsstrang` (Backend) sucht
 * ein To-do mit genau diesem Titel und hängt Notiz und Mail dorthin — der
 * Entwurf würde beim ersten Öffnen der Detailseite zum Sammelbecken.
 */
export function todoDraftTitel(draft: Pick<TodoDraftLike, "titel" | "beschreibung">): string {
    const titel = (draft.titel ?? "").trim();
    if (titel) return titel;
    const zeile = (draft.beschreibung ?? "")
        .split(/\r?\n/)
        .map((z) => z.trim())
        .find(Boolean);
    if (!zeile) return "To-do";
    return zeile.length > TODO_ERSATZTITEL_MAX ? `${zeile.slice(0, TODO_ERSATZTITEL_MAX - 1).trimEnd()}…` : zeile;
}

/**
 * Wahr nur für eine völlig unberührte Karte: kein Betreff, keine Beschreibung,
 * weder TWV noch Frist, Bearbeiter unverändert gleich der Vorgabe und niemand
 * zu informieren. Ein einziges gesetztes Feld macht die Karte „nicht leer".
 */
export function todoDraftIstLeer(draft: TodoDraftLike, vorgabeBearbeiter: string[]): boolean {
    if (draft.titel.trim() || draft.beschreibung.trim()) return false;
    if (draft.twv || draft.frist) return false;
    if (draft.zuInformierende.length > 0) return false;
    const a = [...new Set(draft.bearbeiter)].sort();
    const b = [...new Set(vorgabeBearbeiter)].sort();
    return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Was `buildDraftTodoPayload` vom Flow braucht. */
export interface DraftTodoKontext {
    persoenlich?: boolean;
    linkedEntityType?: LinkedEntityType | null;
    linkedEntityId?: number | null;
}

/** Payload für `POST /todo` aus einem Entwurf (Ersatz-Titel, T-39). */
export function buildDraftTodoPayload(
    draft: TodoDraftLike,
    kontext: DraftTodoKontext,
    vorgangId: number,
    currentUserId: string | null,
): Record<string, unknown> {
    const persoenlich = !!kontext.persoenlich;
    return {
        titel: todoDraftTitel(draft),
        beschreibung: draft.beschreibung.trim() || undefined,
        vorgangId,
        // I1/E07: ein Todo im persönlichen Vorgang trägt keine Bezüge
        // und keine fremden Beteiligten (das Backend lehnt beides ab).
        contactId: persoenlich ? null : draft.slots.kontakt?.id ?? null,
        riskObjectId: persoenlich ? null : draft.slots.risiko?.id ?? null,
        contractId: persoenlich ? null : draft.slots.vertrag?.id ?? null,
        // I4: mehrfach gewählte Risiken/Verträge gingen bisher still verloren —
        // die Arrays sind serverseitig die Quelle der Wahrheit (reconcileMultiLinks).
        riskObjectIds: persoenlich ? [] : draft.slots.risiken.map((r) => r.id),
        contractIds: persoenlich ? [] : draft.slots.vertraege.map((v) => v.id),
        bearbeiter: persoenlich ? (currentUserId ? [currentUserId] : []) : draft.bearbeiter,
        zuInformierende: persoenlich ? [] : draft.zuInformierende,
        twv: draft.twv ?? undefined,
        frist: draft.frist ?? undefined,
        status: "Offen",
        ersteller: currentUserId ?? undefined,
        ...(kontext.linkedEntityType && kontext.linkedEntityId != null
            ? { linkedEntityType: kontext.linkedEntityType, linkedEntityId: kontext.linkedEntityId }
            : {}),
    };
}
